import { useEffect, useState } from "react";
import { Camera, Check, FileText, Flame, Loader2, ShoppingCart, Sparkles, Tag, TrendingUp, Type } from "lucide-react";
import { api, API_BASE, fmt } from "../../lib/api";
import { toast } from "sonner";

// URL d'image produit : absolue telle quelle, sinon servie par l'API (/api/files/…)
const imgUrl = (src) => (!src ? "" : src.startsWith("http") ? src : `${API_BASE}${src}`);

// Carte « Produits chauds » (aperçu admin) : produits les plus CONSULTÉS
// croisés avec les ventes réelles — pour repérer ce qui intéresse les
// visiteurs sans se vendre (badge ambré « Vu mais invendu »). Chaque produit
// a un bouton « Conseils IA » : Claude propose 3 améliorations (titre, photo, prix).
export default function HotProducts() {
  const [data, setData] = useState(null);
  const [advice, setAdvice] = useState({});     // {pid: {loading, title, photo, price}}
  const [openAdvice, setOpenAdvice] = useState("");

  useEffect(() => {
    api.get("/admin/analytics/hot-products", { params: { days: 30 } })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  if (!data || !data.products.length) return null;
  const maxViews = Math.max(1, ...data.products.map((p) => p.views));

  const askAdvice = async (p) => {
    if (openAdvice === p.id) { setOpenAdvice(""); return; }
    setOpenAdvice(p.id);
    if (advice[p.id]?.title || advice[p.id]?.loading) return;
    setAdvice((a) => ({ ...a, [p.id]: { loading: true } }));
    try {
      const { data: d } = await api.post(`/admin/products/${p.id}/ai-advice`,
        { views: p.views, sold: p.sold });
      setAdvice((a) => ({ ...a, [p.id]: d }));
    } catch (err) {
      setAdvice((a) => ({ ...a, [p.id]: null }));
      setOpenAdvice("");
      toast.error(err.response?.data?.detail || "L'IA n'a pas répondu — réessayez");
    }
  };

  // Applique une suggestion IA directement à la fiche produit (1 clic) :
  // titre → name, description → description, prix → base_price/price selon la fiche
  const applyField = async (p, key, patch, doneMsg) => {
    const adv = advice[p.id];
    if (!adv || adv[`applying_${key}`] || adv[`applied_${key}`]) return;
    setAdvice((a) => ({ ...a, [p.id]: { ...a[p.id], [`applying_${key}`]: true } }));
    try {
      await api.patch(`/admin/products/${p.id}`, patch);
      if (patch.name) {
        setData((d) => ({ ...d, products: d.products.map((x) => x.id === p.id ? { ...x, name: patch.name } : x) }));
      }
      setAdvice((a) => ({ ...a, [p.id]: { ...a[p.id], [`applying_${key}`]: false, [`applied_${key}`]: true } }));
      toast.success(doneMsg);
    } catch (err) {
      setAdvice((a) => ({ ...a, [p.id]: { ...a[p.id], [`applying_${key}`]: false } }));
      toast.error(err.response?.data?.detail || "L'application a échoué — réessayez");
    }
  };

  return (
    <div className="mt-8 bg-surface border border-gold/15 rounded-xl p-7" data-testid="hot-products">
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <Flame className="w-4 h-4 text-gold" />
        <h3 className="text-xs uppercase tracking-widest text-ivory/70">
          Produits chauds — {data.days} derniers jours
        </h3>
        <span className="ml-auto text-[11px] text-ivory/55">
          Consultations de la fiche × ventes réelles
        </span>
      </div>
      <ul className="space-y-2">
        {data.products.map((p, i) => {
          const invendu = p.sold === 0 && p.views >= 3;
          const adv = advice[p.id];
          const open = openAdvice === p.id;
          return (
            <li key={p.id} data-testid={`hot-product-${i}`}>
              <div className="relative rounded-md overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-gold/10"
                  style={{ width: `${Math.max(6, (p.views / maxViews) * 100)}%` }} />
                <div className="relative flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2">
                  <span className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold border ${
                    i === 0 ? "bg-gold text-ink border-gold" : i < 3 ? "border-gold/50 text-gold" : "border-ivory/20 text-ivory/50"}`}>
                    {i + 1}
                  </span>
                  {p.image ? (
                    <img src={imgUrl(p.image)} alt="" loading="lazy"
                      className="w-9 h-9 rounded object-cover border border-ivory/10 shrink-0 bg-ink" />
                  ) : (
                    <span className="w-9 h-9 rounded border border-ivory/10 bg-ink shrink-0" />
                  )}
                  <span className="min-w-0 flex-1 basis-44 sm:basis-0">
                    <span className="block text-sm text-ivory/90 break-words" data-testid={`hot-product-name-${i}`}>{p.name}</span>
                    {invendu && (
                      <span className="inline-block mt-0.5 text-[10px] font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/40 rounded-full px-2 py-0.5"
                        data-testid={`hot-product-badge-${i}`}>
                        Vu mais invendu — à travailler
                      </span>
                    )}
                  </span>
                  {/* Conseils IA : 3 améliorations concrètes (titre, photo, prix) */}
                  <button type="button" onClick={() => askAdvice(p)} data-testid={`hot-product-ai-${i}`}
                    title="3 améliorations concrètes proposées par l'IA (titre, photo, prix)"
                    className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] transition-colors duration-300 ${
                      open ? "bg-gold text-ink border-gold font-bold"
                      : invendu ? "border-amber-500/50 text-amber-400 hover:bg-amber-500 hover:text-ink"
                      : "border-ivory/20 text-ivory/55 hover:border-gold/50 hover:text-gold"}`}>
                    {adv?.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    Conseils IA
                  </button>
                  <span className="shrink-0 text-right ml-auto sm:ml-0">
                    <span className="block text-sm text-gold font-semibold" data-testid={`hot-product-views-${i}`}>
                      <TrendingUp className="w-3 h-3 inline-block mr-1 -mt-0.5" />{p.views} <span className="text-[10px] text-ivory/50 font-normal">vue{p.views > 1 ? "s" : ""}</span>
                    </span>
                    <span className={`block text-[11px] ${p.sold > 0 ? "text-emerald-400" : "text-ivory/40"}`} data-testid={`hot-product-sold-${i}`}>
                      <ShoppingCart className="w-3 h-3 inline-block mr-1 -mt-0.5" />{p.sold} vendu{p.sold > 1 ? "s" : ""}
                    </span>
                  </span>
                </div>
              </div>
              {open && adv && !adv.loading && (
                <div className="mt-1.5 ml-9 border border-gold/25 bg-gold/5 rounded-lg p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
                  data-testid={`hot-product-advice-${i}`}>
                  {[
                    ["title", "Titre", Type],
                    ["description", "Description", FileText],
                    ["photo", "Photo", Camera],
                    ["price", "Prix", Tag],
                  ].map(([key, label, Icon]) => (
                    <div key={key}>
                      <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-gold mb-1.5">
                        <Icon className="w-3 h-3" /> {label}
                      </p>
                      <p className="text-xs text-ivory/85 leading-relaxed" data-testid={`advice-${key}-${i}`}>
                        {key === "description" ? (adv.description_suggestion ? "Voir la proposition ci-dessous." : "Description actuelle adéquate selon l'IA.") : (adv[key] || "—")}
                      </p>
                      {/* Titre proposé → appliquer en 1 clic */}
                      {key === "title" && adv.title_suggestion && (
                        <div className="mt-2.5 border border-gold/30 rounded-lg p-2.5 bg-ink" data-testid={`advice-apply-title-${i}`}>
                          <p className="text-[10px] uppercase tracking-widest text-ivory/50 mb-1">Titre proposé</p>
                          <p className="text-xs font-semibold text-gold leading-snug" data-testid={`advice-suggestion-${i}`}>
                            {adv.title_suggestion}
                          </p>
                          {adv.applied_title ? (
                            <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-400" data-testid={`advice-applied-title-${i}`}>
                              <Check className="w-3.5 h-3.5" /> Appliqué à la fiche
                            </p>
                          ) : (
                            <button type="button" disabled={!!adv.applying_title}
                              onClick={() => applyField(p, "title", { name: adv.title_suggestion }, `Fiche renommée : « ${adv.title_suggestion} »`)}
                              data-testid={`advice-apply-title-btn-${i}`}
                              className="mt-2 inline-flex items-center gap-1.5 bg-gold text-ink font-bold px-3.5 py-1.5 rounded-full text-[11px] hover:shadow-[0_0_14px_rgba(212,175,55,0.35)] transition-all duration-300 disabled:opacity-50">
                              {adv.applying_title ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                              Appliquer ce titre
                            </button>
                          )}
                        </div>
                      )}
                      {/* Description proposée → appliquer en 1 clic */}
                      {key === "description" && adv.description_suggestion && (
                        <div className="mt-2.5 border border-gold/30 rounded-lg p-2.5 bg-ink" data-testid={`advice-apply-desc-${i}`}>
                          <p className="text-[10px] uppercase tracking-widest text-ivory/50 mb-1">Description proposée</p>
                          <p className="text-xs text-ivory/90 leading-relaxed" data-testid={`advice-desc-suggestion-${i}`}>
                            {adv.description_suggestion}
                          </p>
                          {adv.applied_description ? (
                            <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-400" data-testid={`advice-applied-desc-${i}`}>
                              <Check className="w-3.5 h-3.5" /> Appliquée à la fiche
                            </p>
                          ) : (
                            <button type="button" disabled={!!adv.applying_description}
                              onClick={() => applyField(p, "description", { description: adv.description_suggestion }, "Description mise à jour")}
                              data-testid={`advice-apply-desc-btn-${i}`}
                              className="mt-2 inline-flex items-center gap-1.5 bg-gold text-ink font-bold px-3.5 py-1.5 rounded-full text-[11px] hover:shadow-[0_0_14px_rgba(212,175,55,0.35)] transition-all duration-300 disabled:opacity-50">
                              {adv.applying_description ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                              Appliquer cette description
                            </button>
                          )}
                        </div>
                      )}
                      {/* Prix proposé → appliquer en 1 clic (pas pour les produits à paliers fixes) */}
                      {key === "price" && adv.price_suggestion > 0 && !adv.has_price_tiers && (
                        <div className="mt-2.5 border border-gold/30 rounded-lg p-2.5 bg-ink" data-testid={`advice-apply-price-${i}`}>
                          <p className="text-[10px] uppercase tracking-widest text-ivory/50 mb-1">Prix proposé</p>
                          <p className="text-xs font-semibold text-gold" data-testid={`advice-price-suggestion-${i}`}>
                            {fmt(adv.price_suggestion)} <span className="text-ivory/50 font-normal">(au lieu de {fmt(adv.price_current)})</span>
                          </p>
                          {adv.applied_price ? (
                            <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-400" data-testid={`advice-applied-price-${i}`}>
                              <Check className="w-3.5 h-3.5" /> Appliqué à la fiche
                            </p>
                          ) : (
                            <button type="button" disabled={!!adv.applying_price}
                              onClick={() => applyField(p, "price", { [adv.price_field]: adv.price_suggestion }, `Prix mis à jour : ${fmt(adv.price_suggestion)}`)}
                              data-testid={`advice-apply-price-btn-${i}`}
                              className="mt-2 inline-flex items-center gap-1.5 bg-gold text-ink font-bold px-3.5 py-1.5 rounded-full text-[11px] hover:shadow-[0_0_14px_rgba(212,175,55,0.35)] transition-all duration-300 disabled:opacity-50">
                              {adv.applying_price ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                              Appliquer ce prix
                            </button>
                          )}
                        </div>
                      )}
                      {key === "price" && adv.has_price_tiers && (
                        <p className="mt-2 text-[10px] text-ivory/45">Prix par paliers fixes — ajustement dans la fiche produit.</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <p className="text-[11px] text-ivory/55 mt-4">
        Un produit très vu mais invendu est une occasion : meilleure photo, prix ou description peuvent débloquer la vente.
      </p>
    </div>
  );
}
